/**
 * audioEngineFactory — the ONE swap point for the dj-daw wasm engine and its
 * compatibility-gated optional sampled bank. The Sound Test card and every
 * other IAudioEngine caller remain untouched by artifact capability changes.
 *
 * The handle carries `kind` + `available` alongside the engine so callers (the card's
 * engine indicator) can display which impl is live WITHOUT polluting the pristine
 * IAudioEngine interface.
 */

import type { IAudioEngine } from './IAudioEngine';
import { WasmAudioEngine } from './WasmAudioEngine';
import { gameLogger } from '../../../../utils/GameLogger';
import type { AudioContextOwner } from './AudioContextOwner';
import { WORKLET_CAPABILITIES } from '../dj-audio-worklet';

/** URL of the H2000 sampled bank index. Copied/verified by the P7 bank build. */
export const H2000_BANK_URL = '/dj-audio/bank/bank.index.json';

export interface AudioEngineHandle {
  engine: IAudioEngine;
  kind: 'synth' | 'wasm';
  available: boolean;
  /** Shared browser device used by synth fallback, WASM, and gameplay music. */
  contextOwner: AudioContextOwner;
}

/** Build the active audio engine, with synth fallback for unsupported paths. */
export function createAudioEngine(): AudioEngineHandle {
  const engine = new WasmAudioEngine();
  if (WORKLET_CAPABILITIES.sampleCommands && WORKLET_CAPABILITIES.sampledFiles) {
    // Start loading the bank in the background; ensureBoot() will await it.
    void engine.loadBank(H2000_BANK_URL).catch((e) => {
      gameLogger.warn('audio', '[audioEngineFactory] bank load failed; engine will fall back to synth cues', e);
    });
  }
  return {
    engine,
    kind: 'wasm',
    available: true,
    contextOwner: engine.audioContextOwner(),
  };
}
